
import React, { RefObject } from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import { translations } from '@/utils/translations';
import LegalSection from './LegalSection';

interface LegalCompanyInfoProps {
  contactRef: RefObject<HTMLHeadingElement>;
  companyDetailsRef: RefObject<HTMLHeadingElement>;
  vatInfoRef: RefObject<HTMLHeadingElement>;
  representativeRef: RefObject<HTMLHeadingElement>;
}

const LegalCompanyInfo: React.FC<LegalCompanyInfoProps> = ({
  contactRef,
  companyDetailsRef,
  vatInfoRef,
  representativeRef
}) => {
  const { currentLanguage } = useLanguage();
  const t = translations[currentLanguage];

  const labels = {
    de: {
      email: 'Email', tel: 'Tel',
      companyName: 'Firmenname', legalForm: 'Rechtsform', legalFormValue: 'Einzelunternehmen',
      seat: 'Firmensitz', seatValue: 'Kriens, Schweiz',
      vatStatus: 'MwSt-Status', vatText: 'Kleinunternehmen gemäss Art. 10 Abs. 2 lit. a MWSTG',
      address: <>Zumhofstrasse<br />6010 Kriens<br />Schweiz</>
    },
    en: {
      email: 'Email', tel: 'Phone',
      companyName: 'Company name', legalForm: 'Legal form', legalFormValue: 'Sole proprietorship',
      seat: 'Registered seat', seatValue: 'Kriens, Switzerland',
      vatStatus: 'VAT status', vatText: 'Small business pursuant to Art. 10 para. 2 let. a Swiss VAT Act',
      address: <>Zumhofstrasse<br />6010 Kriens<br />Switzerland</>
    },
    fr: {
      email: 'E-mail', tel: 'Tél.',
      companyName: "Raison sociale", legalForm: 'Forme juridique', legalFormValue: 'Entreprise individuelle',
      seat: 'Siège social', seatValue: 'Kriens, Suisse',
      vatStatus: 'Statut TVA', vatText: 'Petite entreprise selon art. 10 al. 2 let. a LTVA',
      address: <>Zumhofstrasse<br />6010 Kriens<br />Suisse</>
    },
    it: {
      email: 'Email', tel: 'Tel.',
      companyName: 'Ragione sociale', legalForm: 'Forma giuridica', legalFormValue: 'Ditta individuale',
      seat: 'Sede legale', seatValue: 'Kriens, Svizzera',
      vatStatus: 'Stato IVA', vatText: 'Piccola impresa ai sensi dell\'art. 10 cpv. 2 lett. a LIVA',
      address: <>Zumhofstrasse<br />6010 Kriens<br />Svizzera</>
    }
  }[currentLanguage] ?? undefined;

  const l = labels ?? {
    email: 'Email', tel: 'Tel',
    companyName: 'Firmenname', legalForm: 'Rechtsform', legalFormValue: 'Einzelunternehmen',
    seat: 'Firmensitz', seatValue: 'Kriens, Schweiz',
    vatStatus: 'MwSt-Status', vatText: 'Kleinunternehmen gemäss Art. 10 Abs. 2 lit. a MWSTG',
    address: <>Zumhofstrasse<br />6010 Kriens<br />Schweiz</>
  };

  return (
    <>
      <LegalSection id="contact-info" title={t.contactInfo} titleRef={contactRef}>
        <p className="mb-4 text-gray-300">Future Level</p>
        <p className="mb-4 text-gray-300">{l.address}</p>
        <p className="mb-4 text-gray-300">
          <strong className="text-purple-200">{l.email}:</strong> <a href="mailto:info@future-level.ch" className="text-purple-400 hover:text-purple-300 underline break-all">info@future-level.ch</a><br />
          <strong className="text-purple-200">{l.tel}:</strong> <a href="tel:+41415391613" className="text-purple-400 hover:text-purple-300 underline">+41 41 539 16 13</a>
        </p>
      </LegalSection>
      
      <LegalSection id="company-details" title={t.companyDetails} titleRef={companyDetailsRef}>
        <p className="mb-4 text-gray-300">
          <strong className="text-purple-200">{l.companyName}:</strong> Future Level<br />
          <strong className="text-purple-200">{l.legalForm}:</strong> {l.legalFormValue}<br />
          <strong className="text-purple-200">{l.seat}:</strong> {l.seatValue}
        </p>
      </LegalSection>
      
      <LegalSection id="vat-info" title={t.vatInfo} titleRef={vatInfoRef}>
        <p className="mb-4 text-gray-300">
          <strong className="text-purple-200">{l.vatStatus}:</strong> {l.vatText}
        </p>
      </LegalSection>
      
      <LegalSection id="authorized-representative" title={t.authorizedRepresentative} titleRef={representativeRef}>
        <p className="mb-4 text-gray-300">Future Level</p>
      </LegalSection>
    </>
  );
};

export default LegalCompanyInfo;
